import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Trash2, Save, Info } from 'lucide-react';

const OVERHEAD_CATEGORIES = [
  { key: 'cat_clinic_consumables', label: 'Clinic Consumables' },
  { key: 'cat_front_of_house',     label: 'Front of House' },
  { key: 'cat_janitorial',         label: 'Janitorial' },
  { key: 'cat_back_bar',           label: 'Back Bar' },
  { key: 'cat_emergency',          label: 'Emergency Supplies' },
];

const WEEKS_PER_MONTH = 4.333;

export const loadOverheadSettings = async () => {
  const [fcRes, setRes] = await Promise.all([
    supabase.from('fixed_costs').select('*'),
    supabase.from('settings').select('*'),
  ]);
  const costs = fcRes.data || [];
  const settingsArr = setRes.data || [];
  const settings = Object.fromEntries(settingsArr.map(s => [s.key, s.value]));
  const hoursPerWeek = parseFloat(settings.hours_per_week) || 0;
  const hoursPerMonth = hoursPerWeek * WEEKS_PER_MONTH;
  const totalFixedMonthly = costs.reduce((s, c) => s + (parseFloat(c.monthly_amount) || 0), 0);
  const totalCatMonthly = OVERHEAD_CATEGORIES.reduce((s, c) => s + (parseFloat(settings[c.key]) || 0), 0);
  const totalMonthly = totalFixedMonthly + totalCatMonthly;
  const costPerHour = hoursPerMonth > 0 ? totalMonthly / hoursPerMonth : 0;
  const catCostPerHour = (key) => {
    const monthly = parseFloat(settings[key]) || 0;
    return hoursPerMonth > 0 ? monthly / hoursPerMonth : 0;
  };
  return { hoursPerMonth, hoursPerWeek, totalFixedMonthly, totalCatMonthly, totalMonthly, costPerHour, catCostPerHour, settings };
};

export const calcTreatmentOverhead = async (durationMins) => {
  if (!durationMins) return { fixedOverhead: 0, catOverheads: {}, totalOverhead: 0 };
  const { costPerHour, catCostPerHour } = await loadOverheadSettings();
  const hrs = parseFloat(durationMins) / 60;
  const fixedOverhead = costPerHour * hrs;
  const catOverheads = {};
  OVERHEAD_CATEGORIES.forEach(c => { catOverheads[c.key] = catCostPerHour(c.key) * hrs; });
  const totalOverhead = fixedOverhead + Object.values(catOverheads).reduce((s, v) => s + v, 0);
  return { fixedOverhead, catOverheads, totalOverhead };
};

export default function FixedCosts() {
  const [costs, setCosts] = useState([]);
  const [settings, setSettings] = useState({});
  const [hoursPerWeek, setHoursPerWeek] = useState('');
  const [catBudgets, setCatBudgets] = useState({});
  const [newCost, setNewCost] = useState({ name: '', monthly_amount: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const [fc, s] = await Promise.all([
      supabase.from('fixed_costs').select('*').order('name'),
      supabase.from('settings').select('*'),
    ]);
    setCosts(fc.data || []);
    const sMap = Object.fromEntries((s.data || []).map(r => [r.key, r.value]));
    setSettings(sMap);
    setHoursPerWeek(sMap.hours_per_week || '');
    const budgets = {};
    OVERHEAD_CATEGORIES.forEach(c => { budgets[c.key] = sMap[c.key] || ''; });
    setCatBudgets(budgets);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSetting = async (key, value) => {
    await supabase.from('settings').upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  };

  const saveAll = async () => {
    setSaving(true);
    await saveSetting('hours_per_week', hoursPerWeek);
    for (const [key, val] of Object.entries(catBudgets)) {
      await saveSetting(key, val);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    setSaving(false);
    load();
  };

  const addCost = async () => {
    if (!newCost.name || !newCost.monthly_amount) return;
    await supabase.from('fixed_costs').insert([{
      name: newCost.name,
      monthly_amount: parseFloat(newCost.monthly_amount),
      notes: newCost.notes,
    }]);
    setNewCost({ name: '', monthly_amount: '', notes: '' });
    load();
  };

  const updateCost = async (id, field, value) => {
    await supabase.from('fixed_costs').update({ [field]: field === 'monthly_amount' ? parseFloat(value) : value }).eq('id', id);
    load();
  };

  const delCost = async (id) => {
    if (!window.confirm('Remove this fixed cost?')) return;
    await supabase.from('fixed_costs').delete().eq('id', id);
    load();
  };

  const hoursPerMonth = parseFloat(hoursPerWeek) * WEEKS_PER_MONTH || 0;
  const totalFixed = costs.reduce((s, c) => s + (parseFloat(c.monthly_amount) || 0), 0);
  const totalCat = OVERHEAD_CATEGORIES.reduce((s, c) => s + (parseFloat(catBudgets[c.key]) || 0), 0);
  const totalMonthly = totalFixed + totalCat;
  const costPerHour = hoursPerMonth > 0 ? totalMonthly / hoursPerMonth : 0;
  const fmt = (n) => `£${n.toFixed(2)}`;

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Fixed Costs</h2>
          <p className="section-sub">Overhead costs that feed into treatment pricing</p>
        </div>
        <button className="btn-gold" onClick={saveAll} disabled={saving}>
          <Save size={14} /> {saved ? 'Saved!' : saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>

      {/* Working Hours */}
      <div className="fc-section">
        <div className="fc-section-title">Working Hours</div>
        <div className="fc-hours-row">
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label>Hours worked per week</label>
            <input type="number" step="0.5" value={hoursPerWeek}
              onChange={e => setHoursPerWeek(e.target.value)}
              placeholder="e.g. 35" />
          </div>
          <div className="fc-derived">
            <div className="fc-derived-item">
              <span className="fc-derived-label">Hours / month</span>
              <span className="fc-derived-val">{hoursPerMonth.toFixed(1)}</span>
            </div>
            <div className="fc-derived-item">
              <span className="fc-derived-label">Days / month (8hr)</span>
              <span className="fc-derived-val">{(hoursPerMonth / 8).toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Overhead Items */}
      <div className="fc-section">
        <div className="fc-section-title">Fixed Overhead Costs</div>
        <p className="fc-hint"><Info size={12} /> Rent, rates, insurance, accountant, software, salaries — anything that doesn't vary with treatment volume.</p>

        <div className="fc-table">
          <div className="fc-table-header">
            <span>Item</span><span>Monthly £</span><span>Notes</span><span></span>
          </div>
          {costs.map(c => (
            <div key={c.id} className="fc-table-row">
              <input defaultValue={c.name} onBlur={e => updateCost(c.id, 'name', e.target.value)} className="fc-input" />
              <input type="number" step="0.01" defaultValue={c.monthly_amount}
                onBlur={e => updateCost(c.id, 'monthly_amount', e.target.value)}
                className="fc-input fc-input-num" />
              <input defaultValue={c.notes} onBlur={e => updateCost(c.id, 'notes', e.target.value)} className="fc-input" placeholder="Optional" />
              <button className="icon-btn danger" onClick={() => delCost(c.id)}><Trash2 size={13} /></button>
            </div>
          ))}

          {/* Add new row */}
          <div className="fc-table-row fc-add-row">
            <input value={newCost.name} onChange={e => setNewCost({ ...newCost, name: e.target.value })}
              className="fc-input" placeholder="e.g. Rent" />
            <input type="number" step="0.01" value={newCost.monthly_amount}
              onChange={e => setNewCost({ ...newCost, monthly_amount: e.target.value })}
              className="fc-input fc-input-num" placeholder="0.00" />
            <input value={newCost.notes} onChange={e => setNewCost({ ...newCost, notes: e.target.value })}
              className="fc-input" placeholder="Optional" />
            <button className="btn-gold" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={addCost}>
              <Plus size={13} /> Add
            </button>
          </div>

          <div className="fc-table-total">
            <span>Total fixed overheads</span>
            <span>{fmt(totalFixed)} / month</span>
          </div>
        </div>
      </div>

      {/* Category Monthly Budgets */}
      <div className="fc-section">
        <div className="fc-section-title">Overhead Category Monthly Budgets</div>
        <p className="fc-hint"><Info size={12} /> Enter roughly how much you spend per month on each category. These are pro-rated by treatment duration when calculating treatment costs.</p>

        <div className="fc-table">
          <div className="fc-table-header">
            <span>Category</span><span>Monthly £</span><span>Cost / hour</span>
          </div>
          {OVERHEAD_CATEGORIES.map(({ key, label }) => {
            const monthly = parseFloat(catBudgets[key]) || 0;
            const perHour = hoursPerMonth > 0 ? monthly / hoursPerMonth : 0;
            return (
              <div key={key} className="fc-table-row">
                <span className="fc-cat-label">{label}</span>
                <input type="number" step="0.01"
                  value={catBudgets[key]}
                  onChange={e => setCatBudgets(b => ({ ...b, [key]: e.target.value }))}
                  className="fc-input fc-input-num" placeholder="0.00" />
                <span className="fc-per-hour">{hoursPerMonth > 0 ? `${fmt(perHour)} / hr` : '—'}</span>
              </div>
            );
          })}
          <div className="fc-table-total">
            <span>Total category overheads</span>
            <span>{fmt(totalCat)} / month</span>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="fc-summary">
        <div className="fc-summary-title">Summary</div>
        <div className="fc-summary-grid">
          <div className="fc-summary-item">
            <span>Total monthly overhead</span>
            <strong>{fmt(totalMonthly)}</strong>
          </div>
          <div className="fc-summary-item">
            <span>Working hours / month</span>
            <strong>{hoursPerMonth.toFixed(1)} hrs</strong>
          </div>
          <div className="fc-summary-item highlight">
            <span>Overhead cost per hour</span>
            <strong>{fmt(costPerHour)}</strong>
          </div>
          <div className="fc-summary-item">
            <span>Cost per 30-min treatment</span>
            <strong>{fmt(costPerHour * 0.5)}</strong>
          </div>
          <div className="fc-summary-item">
            <span>Cost per 60-min treatment</span>
            <strong>{fmt(costPerHour)}</strong>
          </div>
          <div className="fc-summary-item">
            <span>Cost per 90-min treatment</span>
            <strong>{fmt(costPerHour * 1.5)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
